// De zware reads van de planningsschermen, via de gedeelde planningCache.
//
// Eén plek per endpoint, zodat Rooster en Capaciteit hetzelfde verzoek ook echt met
// dezelfde sleutel doen (personen; agenda voor dezelfde periode) en een wijziging op het
// ene scherm de juiste sleutels van de andere schermen ongeldig maakt.

import { BACKEND_URL } from './apiConfig';
import { authFetch } from './authHelpers';
import { leesJson } from './apiFout';
import { haal, leesCache, planningSleutel, maakOngeldig, MINUUT } from './planningCache';

export const EP = {
  personen: '/Api/planner-todo/persons',
  ongepland: '/Api/planner-todo/unassigned',
  takenRange: '/Api/person-tasks-range',
  agenda: '/Api/planner-todo/calendar-hours',
  regels: '/Api/planning-rules',
  inplannen: '/Api/inplannen/overzicht',
  dagstart: '/Api/dagstart',
};

/** Endpoints waarvan de inhoud verandert als een taak verschuift, toegewezen of afgevinkt wordt. */
export const TAAK_ENDPOINTS = [EP.ongepland, EP.takenRange, EP.inplannen, EP.dagstart];

const TTL_LANG = 5 * MINUUT; // personen, regels: veranderen zelden
const TTL_KORT = 2 * MINUUT; // taken, agenda: collega's plannen ook
/**
 * Agenda: tot een kwartier oud mag hij direct getoond worden, maar ouder dan 30 s wordt
 * hij meteen op de achtergrond ververst (stale-while-revalidate, bijVerversing werkt het
 * scherm bij). Outlook lezen is het traagste deel van de planning; niemand hoeft erop te
 * wachten als er al een recente stand ligt (Joris, 26 sep).
 */
export const AGENDA_MAX_LEEFTIJD = 15 * MINUUT;
/**
 * Na een verlopen engine-agenda: zo lang wachten voor we nog eens vragen — oplopend, want
 * de engine leest dan nog Outlook. Na de laatste stoppen we; het volgende bezoek probeert
 * het opnieuw (een verlopen antwoord wordt niet bewaard).
 */
export const AGENDA_NAVRAAG_MS = [3000, 6000, 12000, 24000];

export const sleutels = {
  personen: () => planningSleutel(EP.personen),
  ongepland: () => planningSleutel(EP.ongepland),
  takenRange: ({ personIds, startDate, endDate }) => planningSleutel(EP.takenRange, { personIds, startDate, endDate }),
  agenda: ({ emails, startDate, endDate }) => planningSleutel(EP.agenda, {
    emails: [...new Set((emails || []).map((e) => String(e || '').trim().toLowerCase()).filter(Boolean))],
    startDate,
    endDate,
    includeEvents: true,
  }),
  regels: () => planningSleutel(EP.regels),
  inplannen: () => planningSleutel(EP.inplannen),
};

/** Personen (Rooster, Capaciteit, Dagstart delen dezelfde sleutel). */
export function haalPersonen({ forceer = false, bijVerversing } = {}) {
  return haal(sleutels.personen(), async () => {
    const r = await authFetch(`${BACKEND_URL}${EP.personen}`);
    const d = await leesJson(r, 'Personen laden');
    return d?.persons || [];
  }, { maxLeeftijd: TTL_LANG, forceer, bijVerversing });
}

/** Nog niet ingeplande taken (Rooster-bak). */
export function haalOngepland({ forceer = false, bijVerversing } = {}) {
  return haal(sleutels.ongepland(), async () => {
    const r = await authFetch(`${BACKEND_URL}${EP.ongepland}`);
    return leesJson(r, 'Ongeplande taken laden');
  }, { maxLeeftijd: TTL_KORT, forceer, bijVerversing });
}

/** Ingeplande taken per persoon-id over een periode. */
export function haalTakenRange({ personIds, startDate, endDate }, { forceer = false, bijVerversing } = {}) {
  return haal(sleutels.takenRange({ personIds, startDate, endDate }), async () => {
    const params = new URLSearchParams({ startDate, endDate });
    (personIds || []).forEach((id) => params.append('personIds[]', id));
    const r = await authFetch(`${BACKEND_URL}${EP.takenRange}?${params}`);
    return (await leesJson(r, 'Ingeplande taken laden')) || {};
  }, { maxLeeftijd: TTL_KORT, forceer, bijVerversing });
}

/** Mailboxen die de engine niet kon lezen (403/throttle) — {email: reden}, kleine letters. */
export function nietGelezen(agenda) {
  const uit = {};
  Object.entries(agenda?.niet_gelezen || {}).forEach(([e, v]) => { uit[String(e).toLowerCase()] = v || 'niet gelezen'; });
  return uit;
}

/**
 * Agenda-uren (+ afspraken, afwezig) uit Outlook. Ruwe engine-vorm:
 * { hours, events, afwezig, niet_gelezen }. `forceer` stuurt vers=1 (engine-cache omzeilen).
 * Een antwoord met niet-gelezen mailboxen wordt wél teruggegeven maar niet bewaard: dan
 * probeert het volgende bezoek het opnieuw i.p.v. "iedereen vrij" te blijven tonen.
 */
/** Eén keer de agenda bij de engine lezen (`vers` = ook de engine-cache overslaan). */
async function leesAgendaDirect({ emails, startDate, endDate }, vers = false) {
  const lijst = [...new Set((emails || []).map((e) => String(e || '').trim().toLowerCase()).filter(Boolean))];
  const r = await authFetch(`${BACKEND_URL}${EP.agenda}${vers ? '?vers=1' : ''}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ emails: lijst, startDate, endDate, includeEvents: true }),
  });
  const d = await leesJson(r, 'Agenda-uren laden');
  return {
    hours: d?.hours || {},
    events: d?.events || {},
    afwezig: d?.afwezig || {},
    niet_gelezen: d?.niet_gelezen || {},
    // Mailboxen die de engine uit zijn verlopen cache gaf (en nu zelf ververst).
    verouderd: Array.isArray(d?.verouderd) ? d.verouderd : [],
  };
}

// Niet bewaren: niet-gelezen mailboxen (dan "iedereen vrij") en een verlopen engine-stand
// (die mag geen nieuwe tijdstempel krijgen).
const agendaMagBewaren = (a) => !Object.keys(a?.niet_gelezen || {}).length && !(a?.verouderd || []).length;

export function haalAgenda({ emails, startDate, endDate }, { forceer = false, bijVerversing, signaal } = {}) {
  const args = { emails, startDate, endDate };
  const sleutel = sleutels.agenda(args);
  const lees = (vers) => () => leesAgendaDirect(args, vers);
  const belofte = haal(sleutel, lees(forceer), {
    maxLeeftijd: AGENDA_MAX_LEEFTIJD,
    forceer,
    bijVerversing,
    // Achtergrondverversing van een oudere stand: de engine óók vers laten lezen, anders
    // komt zijn eigen verlopen cache terug en krijgt die hier een nieuwe tijdstempel.
    verversFetcher: lees(true),
    magBewaren: agendaMagBewaren,
  });
  // Gaf de engine (deels) een verlopen stand, dan ververst hij die op de achtergrond:
  // begrensd doorvragen (3, 6, 12, 24 s) tot er een vers antwoord is, en dan het scherm via
  // bijVerversing bijwerken. `signaal` (AbortSignal van het scherm) stopt het doorvragen
  // zodra het scherm weg is of iets anders vraagt: geen lekkende timers.
  if (bijVerversing) {
    belofte.then((res) => {
      if ((res?.verouderd || []).length) vraagDoor(args, bijVerversing, signaal);
    }).catch(() => {});
  }
  return belofte;
}

function vraagDoor(args, bijVerversing, signaal, poging = 0) {
  if (signaal?.aborted || poging >= AGENDA_NAVRAAG_MS.length) return;
  const timer = setTimeout(() => {
    signaal?.removeEventListener?.('abort', stop);
    if (signaal?.aborted) return;
    haal(sleutels.agenda(args), async () => leesAgendaDirect(args), {
      maxLeeftijd: AGENDA_MAX_LEEFTIJD, magBewaren: agendaMagBewaren,
    })
      .then((nieuw) => {
        if (signaal?.aborted) return;
        if ((nieuw?.verouderd || []).length) vraagDoor(args, bijVerversing, signaal, poging + 1);
        else bijVerversing(nieuw);
      })
      .catch(() => { /* stil: de getoonde stand blijft staan */ });
  }, AGENDA_NAVRAAG_MS[poging]);
  function stop() { clearTimeout(timer); }
  signaal?.addEventListener?.('abort', stop, { once: true });
}

/** Wat er voor deze agenda-aanvraag al in de cache ligt (tot AGENDA_MAX_LEEFTIJD oud), of null. */
export function leesAgendaCache(args) {
  return leesCache(sleutels.agenda(args), { maxLeeftijd: AGENDA_MAX_LEEFTIJD });
}

/** Capaciteitsregels (planner). */
export function haalRegels({ forceer = false, bijVerversing } = {}) {
  return haal(sleutels.regels(), async () => {
    const r = await authFetch(`${BACKEND_URL}${EP.regels}`);
    return leesJson(r, 'Capaciteitsregels laden');
  }, { maxLeeftijd: TTL_LANG, forceer, bijVerversing });
}

/** "Nog in te plannen"-overzicht. `forceer` = engine laten herberekenen (vers=true). */
export function haalInplannen({ forceer = false, bijVerversing, herberekenen = forceer } = {}) {
  // `forceer` slaat de cache van de pagina over; `herberekenen` (standaard hetzelfde) laat
  // ook de engine opnieuw rekenen. Nog eens vragen of de achtergrondberekening klaar is:
  // forceer zonder herberekenen.
  return haal(sleutels.inplannen(), async () => {
    const r = await authFetch(`${BACKEND_URL}${EP.inplannen}${herberekenen ? '?vers=true' : ''}`);
    return leesJson(r, 'Nog in te plannen laden');
  }, { maxLeeftijd: TTL_KORT, forceer, bijVerversing });
}

/**
 * Na een schrijfactie op taken: de takenstanden van alle planningsschermen vervallen, zodat
 * een andere tab geen verouderde stand toont. `behalve`: de sleutels die het huidige scherm
 * zelf optimistisch bijhoudt (zie werkCacheBij).
 */
export function taakGewijzigd({ behalve = [] } = {}) {
  maakOngeldig(TAAK_ENDPOINTS, { behalve });
}

/**
 * Na elke taakmutatie buiten de planningstabs (Vandaag, projectdetail, …) en na elke undo
 * daarvan: de takenstanden van Rooster, Capaciteit, Nog in te plannen en Dagstart vervallen.
 * Geeft `resultaat` door, zodat hij in een keten past: `.then(naTaakMutatie)`.
 */
export function naTaakMutatie(resultaat) {
  taakGewijzigd();
  return resultaat;
}

/**
 * authFetch voor een schrijfactie op /Api/tasks (PATCH/POST/DELETE). Maakt de gedeelde
 * planningcache ongeldig zodra er antwoord is — ook bij een fout: een half gelukte schrijf
 * mag geen verouderde stand achterlaten, en een extra verversing kost niets.
 */
export async function schrijfTaak(url, opties) {
  try {
    return await authFetch(url, opties);
  } finally {
    taakGewijzigd();
  }
}
